import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// API Base URL - change this to your server URL
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Calming color palette - sage greens, soft blues, warm neutrals
const styles = {
  // Global styles
  global: `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
      background: linear-gradient(135deg, #e8f0e9 0%, #d4e5d8 50%, #c5dde0 100%);
      min-height: 100vh;
      color: #3d4f4f;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-8px); }
    }
  `,
  
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
  },
  
  bgCircle1: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'rgba(124, 152, 133, 0.15)',
    top: '-100px',
    right: '-100px',
    animation: 'float 8s ease-in-out infinite',
  },
  
  bgCircle2: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'rgba(147, 180, 186, 0.12)',
    bottom: '-80px',
    left: '-80px',
    animation: 'float 10s ease-in-out infinite 1s',
  },
  
  bgCircle3: {
    position: 'absolute',
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    background: 'rgba(200, 180, 160, 0.1)',
    top: '40%',
    left: '10%',
    animation: 'float 6s ease-in-out infinite 0.5s',
  },
  
  card: {
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 8px 32px rgba(61, 79, 79, 0.08), 0 2px 8px rgba(61, 79, 79, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    animation: 'fadeIn 0.6s ease-out',
    position: 'relative',
    zIndex: 1,
  },
  
  logo: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  
  logoText: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#3d4f4f',
    letterSpacing: '-0.5px',
  },
  
  subtitle: {
    fontSize: '15px',
    color: '#6b8080',
    marginTop: '8px',
    fontWeight: '400',
  },
  
  tabs: {
    display: 'flex',
    gap: '4px',
    background: 'rgba(124, 152, 133, 0.1)',
    padding: '4px',
    borderRadius: '14px',
    marginBottom: '32px',
  },
  
  tab: {
    flex: 1,
    padding: '12px 20px',
    border: 'none',
    background: 'transparent',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b8080',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
  },
  
  tabActive: {
    background: 'white',
    color: '#3d4f4f',
    boxShadow: '0 2px 8px rgba(61, 79, 79, 0.08)',
  },
  
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  
  inputGroup: {
    position: 'relative',
  },
  
  nameRow: {
    display: 'flex',
    gap: '12px',
  },
  
  nameField: {
    flex: 1,
  },
  
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#5a7070',
    marginBottom: '8px',
    letterSpacing: '0.3px',
  },
  
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '12px',
    fontSize: '15px',
    color: '#3d4f4f',
    background: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
    outline: 'none',
    fontFamily: 'inherit',
  },
  
  inputFocus: {
    borderColor: '#7c9885',
    background: 'white',
    boxShadow: '0 0 0 4px rgba(124, 152, 133, 0.1)',
  },
  
  roleGroup: {
    display: 'flex',
    gap: '12px',
  },
  
  roleOption: {
    flex: 1,
    position: 'relative',
  },
  
  roleInput: {
    position: 'absolute',
    opacity: 0,
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    zIndex: 2,
  },
  
  roleLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 12px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    background: 'rgba(255, 255, 255, 0.8)',
  },
  
  roleLabelSelected: {
    borderColor: '#7c9885',
    background: 'rgba(124, 152, 133, 0.1)',
    boxShadow: '0 0 0 4px rgba(124, 152, 133, 0.1)',
  },
  
  roleIcon: {
    fontSize: '24px',
    marginBottom: '8px',
  },
  
  roleText: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#3d4f4f',
    textAlign: 'center',
  },
  
  button: {
    padding: '16px 24px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    marginTop: '8px',
    position: 'relative',
    overflow: 'hidden',
  },
  
  buttonPrimary: {
    background: 'linear-gradient(135deg, #7c9885 0%, #6a8a74 100%)',
    color: 'white',
    boxShadow: '0 4px 12px rgba(124, 152, 133, 0.35)',
  },
  
  message: {
    padding: '14px 16px',
    borderRadius: '12px',
    fontSize: '14px',
    textAlign: 'center',
    animation: 'fadeIn 0.3s ease-out',
    marginBottom: '20px',
  },
  
  messageError: {
    background: 'rgba(180, 120, 120, 0.12)',
    color: '#8a5a5a',
    border: '1px solid rgba(180, 120, 120, 0.2)',
  },
  
  messageSuccess: {
    background: 'rgba(124, 152, 133, 0.12)',
    color: '#5a7a65',
    border: '1px solid rgba(124, 152, 133, 0.2)',
  },
  
  loading: {
    display: 'inline-block',
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderRadius: '50%',
    borderTopColor: 'white',
    animation: 'spin 0.8s linear infinite',
  },
};

// Component receives setUser from App.js
function AuthPage({ setUser }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [focusedInput, setFocusedInput] = useState('');

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setRole('');
  };

  // Navigate based on user role and signup status
  const navigateUser = (user) => {
    if (user.signUpFinished) {
      navigate('/home');
    } else if (user.role === 'job_seeker') {
      navigate('/createcv');
    } else {
      navigate('/createjoblisting');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });
    
    if (!isLogin) {
      if (password !== confirmPassword) {
        setMessage({ text: 'Passwords do not match', type: 'error' });
        return;
      }
      if (!role) {
        setMessage({ text: 'Please select your role', type: 'error' });
        return;
      }
    }

    setLoading(true);
    const endpoint = isLogin ? 'login' : 'register';

    const body = isLogin 
      ? { email, password }
      : { email, password, firstName, lastName, role };

    try {
      const response = await fetch(`${API_URL}/auth/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.success) {
        // Save token
        localStorage.setItem('token', data.data.token);
        // Update user state in App.js
        setUser(data.data.user);
        // Reset form
        resetForm();
        // Navigate to appropriate page
        navigateUser(data.data.user);
      } else {
        setMessage({ text: data.message, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Unable to connect to server', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getInputStyle = (inputName) => ({
    ...styles.input,
    ...(focusedInput === inputName ? styles.inputFocus : {})
  });

  return (
    <>
      <style>{styles.global}</style>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        input::placeholder {
          color: #9ab0a5;
        }
        button:hover {
          transform: translateY(-2px);
        }
        button:active {
          transform: translateY(0);
        }
      `}</style>
      
      <div style={styles.container}>
        <div style={styles.bgCircle1}></div>
        <div style={styles.bgCircle2}></div>
        <div style={styles.bgCircle3}></div>
        
        <div style={styles.card}>
          <div style={styles.logo}>
            <h1 style={styles.logoText}>Instaview</h1>
            <p style={styles.subtitle}>A job interview in under 5 minutes</p>
          </div>

          {message.text && (
            <div style={{
              ...styles.message,
              ...(message.type === 'error' ? styles.messageError : styles.messageSuccess)
            }}>
              {message.text}
            </div>
          )}

          <div style={styles.tabs}>
            <button
              style={{ ...styles.tab, ...(isLogin ? styles.tabActive : {}) }}
              onClick={() => {
                setIsLogin(true);
                setMessage({ text: '', type: '' });
              }}
            >
              Sign In
            </button>
            <button
              style={{ ...styles.tab, ...(!isLogin ? styles.tabActive : {}) }}
              onClick={() => {
                setIsLogin(false);
                setMessage({ text: '', type: '' });
              }}
            >
              Create Account
            </button>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            {!isLogin && (
              <div style={styles.nameRow}>
                <div style={styles.nameField}>
                  <label style={styles.label}>First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onFocus={() => setFocusedInput('firstName')}
                    onBlur={() => setFocusedInput('')}
                    style={getInputStyle('firstName')}
                    placeholder="John"
                    required
                  />
                </div>
                <div style={styles.nameField}>
                  <label style={styles.label}>Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onFocus={() => setFocusedInput('lastName')}
                    onBlur={() => setFocusedInput('')}
                    style={getInputStyle('lastName')}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>
            )}

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedInput('email')}
                onBlur={() => setFocusedInput('')}
                style={getInputStyle('email')}
                placeholder="you@example.com"
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedInput('password')}
                onBlur={() => setFocusedInput('')}
                style={getInputStyle('password')}
                placeholder="Enter your password"
                required
                minLength={6}
              />
            </div>

            {!isLogin && (
              <>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setFocusedInput('confirm')}
                    onBlur={() => setFocusedInput('')}
                    style={getInputStyle('confirm')}
                    placeholder="Confirm your password"
                    required
                    minLength={6}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>I am a...</label>
                  <div style={styles.roleGroup}>
                    <div style={styles.roleOption}>
                      <input
                        type="radio"
                        name="role"
                        value="job_seeker"
                        checked={role === 'job_seeker'}
                        onChange={(e) => setRole(e.target.value)}
                        style={styles.roleInput}
                        required
                      />
                      <div style={{
                        ...styles.roleLabel,
                        ...(role === 'job_seeker' ? styles.roleLabelSelected : {})
                      }}>
                        <span style={styles.roleIcon}>🔍</span>
                        <span style={styles.roleText}>Job Seeker</span>
                      </div>
                    </div>
                    <div style={styles.roleOption}>
                      <input
                        type="radio"
                        name="role"
                        value="hiring_manager"
                        checked={role === 'hiring_manager'}
                        onChange={(e) => setRole(e.target.value)}
                        style={styles.roleInput}
                        required
                      />
                      <div style={{
                        ...styles.roleLabel,
                        ...(role === 'hiring_manager' ? styles.roleLabelSelected : {})
                      }}>
                        <span style={styles.roleIcon}>💼</span>
                        <span style={styles.roleText}>Hiring Manager</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.button, ...styles.buttonPrimary }}
            >
              {loading ? (
                <span style={styles.loading}></span>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

export default AuthPage;